import { formatReportCurrency, formatReportDate } from '../reports/formatting';
import { providerForSourceSystem } from './providerCatalog';
import type { BatchStatus, MappingMethod } from './types';

export const formatMoney = formatReportCurrency;
export const formatDate = formatReportDate;

export function formatDateRange(start: string | null, end: string | null): string {
  if (!start && !end) return '—';
  if (start && end && start !== end) return `${formatDate(start)} – ${formatDate(end)}`;
  return formatDate(start ?? end ?? '');
}

export function formatDateTime(isoUtc: string | null): string {
  if (!isoUtc) return '—';
  const d = new Date(isoUtc);
  if (Number.isNaN(d.getTime())) return isoUtc;
  return d.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function formatFileSize(bytes: number | null): string {
  if (bytes === null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  const mb = bytes / (1024 * 1024);
  return `${Number.isInteger(mb) ? mb : mb.toFixed(1)} MB`;
}

export function shortId(id: string): string {
  return id.length > 8 ? id.slice(0, 8) : id;
}

export const SOURCE_SYSTEM_LABELS: Record<string, string> = {
  EscochexPayroll: 'Escochex Payroll',
  EpnStandardTemplate: 'EPN Standard Template (Excel)',
  GenericMapped: 'Spreadsheet or CSV (guided mapping)',
};

/** A readable name for a stored source system. "GenericMapped:ADP" is a spreadsheet from ADP that was imported
 * with guided mapping; it is never described as a native ADP import. */
export function sourceSystemLabel(id: string): string {
  const known = SOURCE_SYSTEM_LABELS[id];
  if (known) return known;
  const provider = providerForSourceSystem(id);
  if (provider?.supportLevel === 'NativeTemplate') return provider.displayName;
  if (provider && id.startsWith('GenericMapped:')) {
    return provider.id === 'OtherGuided' ? SOURCE_SYSTEM_LABELS.GenericMapped! : `${provider.displayName} (guided mapping)`;
  }
  return id;
}

type BadgeTone = 'ok' | 'caution' | 'blocked' | 'neutral';
export interface StatusMeta {
  label: string;
  tone: BadgeTone;
  /** Non-color cue so state never relies on color alone. */
  icon: string;
}

export const STATUS_META: Record<BatchStatus, StatusMeta> = {
  Created: { label: 'Draft', tone: 'neutral', icon: '○' },
  Uploaded: { label: 'Uploaded', tone: 'neutral', icon: '◔' },
  Validating: { label: 'Validating', tone: 'neutral', icon: '◔' },
  ColumnMappingRequired: { label: 'Needs Column Mapping', tone: 'caution', icon: '!' },
  ValidationFailed: { label: 'Validation Failed', tone: 'blocked', icon: '✕' },
  ReadyForReview: { label: 'Ready for Review', tone: 'ok', icon: '◐' },
  Importing: { label: 'Importing', tone: 'neutral', icon: '◔' },
  Completed: { label: 'Completed', tone: 'ok', icon: '✓' },
  CompletedWithWarnings: { label: 'Completed With Warnings', tone: 'caution', icon: '✓!' },
  Failed: { label: 'Failed', tone: 'blocked', icon: '✕' },
  Canceled: { label: 'Canceled', tone: 'neutral', icon: '–' },
};

/** Statuses from which a batch can still be canceled (everything before Importing). */
export const CANCELABLE_STATUSES: BatchStatus[] = ['Created', 'Uploaded', 'Validating', 'ColumnMappingRequired', 'ValidationFailed', 'ReadyForReview'];

export const TERMINAL_STATUSES: BatchStatus[] = ['Completed', 'CompletedWithWarnings', 'Failed', 'Canceled'];

/** "Zip package (.zip)", "Excel workbook (.xlsx)" or "CSV file (.csv)" for a batch's file. */
export function fileTypeLabel(batch: { fileFormat: string | null; fileName: string | null }): string {
  const extension = batch.fileName?.toLowerCase().split('.').pop() ?? '';
  switch (batch.fileFormat ?? (extension === 'zip' ? 'Zip' : extension === 'csv' ? 'Csv' : extension === 'xlsx' ? 'Workbook' : '')) {
    case 'Zip': return 'Zip package (.zip)';
    case 'Workbook': return 'Excel workbook (.xlsx)';
    case 'Csv': return 'CSV file (.csv)';
    default: return 'Unknown';
  }
}

const DETECTED_TYPE_WORDS: Record<string, string> = { Number: 'numbers', Date: 'dates', Text: 'text', YesNo: 'yes/no', Empty: 'empty' };
/** The plain word for a detected column type. */
export const detectedTypeWord = (detected: string): string => DETECTED_TYPE_WORDS[detected] ?? detected;

/** How an employee match was made, in plain words. A name never makes a match: only an ID, a saved mapping or the person. */
export function mappingMethodLabel(method: MappingMethod): string {
  switch (method) {
    case 'UserConfirmed': return 'confirmed by you';
    case 'StoredMapping': return 'saved mapping';
    case 'TemplateEmployeeId': return 'Matched by the EPN employee ID in your file';
    default: return 'matched by employee id';
  }
}
